import Header from "@/components/Header";
import Footer from "@/components/Footer";

const CARD = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" };

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl p-5 space-y-3" style={CARD}>
      <h2 className="text-white font-bold text-[15px]">{title}</h2>
      <div className="text-slate-400 text-[13px] leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

function Highlight({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl px-4 py-3 text-[13px] text-emerald-300 leading-relaxed"
      style={{ background: "rgba(52,211,153,0.07)", border: "1px solid rgba(52,211,153,0.2)" }}>
      {children}
    </div>
  );
}

export default function Privacy() {
  return (
    <div className="ls-shell min-h-screen flex flex-col">
      <Header />

      <div className="px-5 pt-8 pb-5" style={{ background: "linear-gradient(180deg,#041a2e 0%,#030a14 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-slate-500 text-[11px] uppercase tracking-widest font-bold mb-2">LiveSwell</p>
          <h1 className="text-white font-black text-2xl leading-tight">Privacy Policy</h1>
          <p className="text-slate-500 text-[12px] mt-1">Effective date: July 1, 2025 &nbsp;·&nbsp; Last updated: October 14, 2026</p>
        </div>
      </div>

      <main className="flex-1 px-4 pt-5 pb-10 max-w-3xl mx-auto w-full space-y-3">

        <Section title="About LiveSwell">
          <p>
            LiveSwell ("we," "us," or "our") is a surf conditions platform that delivers real-time wave,
            wind, and tide data to surfers. This Privacy Policy explains what information we collect,
            how we use it, and your choices — including Agent chat, SMS text messages, and subscriptions.
          </p>
        </Section>

        <Section title="Information we collect">
          <p>We collect the following personal information when you create an account or use the service:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li><strong className="text-slate-300">Account details</strong> — name, email, account identifiers, and sign-in information handled through Clerk; email is also used for email notifications</li>
            <li><strong className="text-slate-300">Phone number and verification records</strong> — when you set up SMS alerts or Agent texting; a number may also be collected during sign-in through Clerk</li>
            <li><strong className="text-slate-300">Saved surf spots and preferences</strong> — including alert settings, delivery channels, and your confirmed home time zone</li>
            <li><strong className="text-slate-300">Conversations</strong> — Agent questions and replies, recent conversation context, and SMS delivery and opt-out records; in-app and SMS histories are separate</li>
            <li><strong className="text-slate-300">Billing and usage records</strong> — Paddle customer and subscription identifiers, plan and payment status, invoices, reply and message-segment usage, and retained historical overage-consent and spending-cap records</li>
            <li><strong className="text-slate-300">Device and service data</strong> — push subscription endpoints or device tokens, app activity, and error diagnostics that may include your account identifier and email</li>
            <li><strong className="text-slate-300">Optional location and support details</strong> — coordinates when you use nearby-spot features and grant location permission, and the name, email, and message you submit for support</li>
          </ul>
        </Section>

        <Section title="SMS messaging — opt-in and consent">
          <Highlight>
            SMS alerts require number verification and your selected SMS settings. Agent texting uses
            the verified number associated with your account. Providing a number for sign-in alone
            does not enable surf alerts.
          </Highlight>
          <p>
            SMS messages we send include:
          </p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Real-time surf condition alerts for your saved spots</li>
            <li>Scheduled surf reports and tomorrow's forecast alerts (if enabled)</li>
            <li>A one-time verification code when you verify your SMS number</li>
            <li>Optional automated replies when you text us a surf question</li>
          </ul>
          <p>
            <strong className="text-slate-300">Message frequency:</strong> Alert frequency depends on
            your schedule, qualifying conditions, and your plan's daily and monthly allowances.
            Agent conversational replies have a separate billing-month allowance.
            See Profile → Account &amp; Billing and Profile → Notifications for usage and settings.
          </p>
          <p>
            <strong className="text-slate-300">Message and data rates may apply.</strong> Contact your
            wireless carrier for details about your plan.
          </p>
        </Section>

        <Section title="SMS opt-out and help">
          <Highlight>
            Reply <strong>STOP</strong> to a LiveSwell SMS to opt out of texts to that number.
            Carrier opt-out blocks SMS delivery.
            Reply <strong>HELP</strong> for assistance.
          </Highlight>
          <p>
            You can also pause or delete an alert, or turn off its SMS channel, under Profile → Notifications.
            This does not necessarily turn off its selected email or push delivery, or Agent texting.
            Opting out via SMS or the app does not delete your account. A carrier-level START reply
            does not automatically restore alert settings in LiveSwell; review your Notification
            Settings before expecting alerts to resume.
          </p>
        </Section>

        <Section title="Agent chat and AI processing">
          <p>
            When you ask Agent a question in the app or by SMS, we send your question, limited recent
            conversation context, and relevant spot or conditions information to Anthropic's Claude
            to help generate the answer. Claude also helps generate surf condition summaries.
            Our Agent requests do not attach your phone number or LiveSwell account identifier as
            separate fields. Anything you type into a question, including personal details, can be
            included in the text sent to Anthropic; do not include passwords, verification codes,
            payment details, or other sensitive information.
          </p>
          <p>
            In-app chat and SMS have separate histories. New chat clears the stored in-app conversation,
            not SMS records or copies already processed by providers. AI replies may be incomplete or
            inaccurate and are not safety advice.
          </p>
        </Section>

        <Section title="Subscriptions and billing">
          <p>
            Paddle handles LiveSwell web checkout, monthly subscriptions, and payment details. Pro is
            $8 USD per month and Agent is $20 USD per month, plus applicable taxes. Eligible new
            subscribers may receive a seven-day trial; eligibility is determined at checkout and repeat
            trials are not promised. Subscriptions renew monthly after any trial until canceled.
            LiveSwell uses account identifiers and, where needed, email to link Paddle billing to your
            account, and keeps subscription, payment-status, invoice, usage, and billing-operation
            records to manage access and charges.
          </p>
          <p>
            A renewal payment failure on a previously paid subscription receives a 72-hour grace
            period. This applies only to a confirmed previously paid renewal, not an initial payment or
            a payment due when a trial ends. Pro-to-Agent upgrades in the native iOS purchase flow are
            prorated. For custom Paddle Agent-to-Pro downgrades, Agent
            access is retained through the original paid boundary; Paddle may replace the subscription
            item earlier. The one-hour LiveSwell application cutoff describes application behavior and
            is not a guarantee of provider timing.
          </p>
          <p>
            Manage billing through Profile → Account &amp; Billing. Collection for extra Agent SMS
            replies is currently disabled; historical usage, invoices, consent, and cap records may be
            retained, but they do not enable new collection. Deleting an alert or opting out of SMS does
            not cancel a subscription.
          </p>
        </Section>

        <Section title="How we use your information">
          <p>We use your information to:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Operate LiveSwell and deliver the alerts you configured</li>
            <li>Verify your phone number before sending SMS messages</li>
            <li>Respond to your in-app and SMS questions about surf conditions</li>
            <li>Manage subscriptions, access, usage allowances, and billing history</li>
            <li>Deliver email and push notifications, answer support requests, and honor SMS opt-outs</li>
            <li>Maintain sign-in, detect errors, and prevent duplicate messages or unauthorized access</li>
            <li>Improve data accuracy and app performance</li>
          </ul>
          <p>
            <strong className="text-slate-300">We do not sell your personal information for advertising.</strong>
            We share information with service providers as described below to operate LiveSwell.
          </p>
        </Section>

        <Section title="Third-party service providers">
          <p>
            Services used to operate LiveSwell include the following. Their processing and retention
            are also subject to their own policies; our local cleanup does not delete their records.
          </p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li><strong className="text-slate-300">Clerk</strong> — identity, sign-in, and session management using account and authentication details</li>
            <li><strong className="text-slate-300">Twilio and mobile carriers</strong> — phone numbers, message content, verification texts, delivery information, and SMS opt-outs (<a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-400 underline hover:text-emerald-300">Twilio privacy policy</a>)</li>
            <li><strong className="text-slate-300">Resend</strong> — recipient email addresses and email content for notifications and support messages</li>
            <li><strong className="text-slate-300">Anthropic (Claude)</strong> — Agent questions and recent context from in-app and SMS conversations, and surf conditions used for answers and summaries</li>
            <li><strong className="text-slate-300">Paddle</strong> — web checkout, payment processing, monthly subscriptions, invoices, and account-linked billing records</li>
            <li><strong className="text-slate-300">NOAA, OpenWeatherMap, Open-Meteo, and WorldTides</strong> — observations, weather, marine forecasts, and tide predictions; requests use spot coordinates, station identifiers, or forecast dates rather than your chat messages or billing details</li>
            <li><strong className="text-slate-300">Apple, Google, and browser push services</strong> — device tokens or subscription endpoints and notification content when push is enabled and available</li>
            <li><strong className="text-slate-300">Sentry</strong> — app error monitoring and diagnostics, which may include your account identifier and email</li>
            <li><strong className="text-slate-300">Hosting and database infrastructure</strong> — storing and processing the account, messaging, and service records needed to run LiveSwell</li>
          </ul>
          <p>
            Messaging requires sharing information with delivery providers and carriers. We cannot
            promise that provider-held records are erased when you clear a chat or delete your account.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            Account details, saved spots, preferences, and usage records are stored to operate your
            account. Different records have different cleanup rules; there is no single retention
            period for all service or provider records.
          </p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li><strong className="text-slate-300">In-app Agent history</strong> — the app shows up to 100 messages from the last 24 hours. Scheduled cleanup targets older messages on startup and daily; New chat clears your stored in-app history.</li>
            <li><strong className="text-slate-300">SMS conversations</strong> — replies use recent context from the last 24 hours. Scheduled cleanup targets conversation jobs and related receipts or alert context older than seven days, except jobs still generating or sending. Older phone-based threads are targeted after seven days of inactivity. Cleanup runs on startup and daily, not exactly at a record's expiry time.</li>
            <li><strong className="text-slate-300">Verification codes</strong> — codes used to verify your LiveSwell SMS number expire after 10 minutes; expiry is not a promise that every associated record is deleted at that moment.</li>
            <li><strong className="text-slate-300">Other records</strong> — SMS opt-out protection, hashed deleted-account identifiers, and some billing-operation records can outlast chat history or account deletion. Provider-held messages, payment records, diagnostics, and backups have separate retention rules.</li>
          </ul>
          <p>
            You can request permanent account deletion from{" "}
            <strong className="text-slate-300">Profile → Delete my account</strong>.
            LiveSwell may need to cancel an attached Paddle subscription as part of account closure, then removes local account,
            saved-spot, alert, and conversation data and deletes your Clerk identity.
            If cancellation or sign-in removal cannot be confirmed, follow the error instructions
            or contact support; do not assume deletion finished. Clearing chat or deleting an account
            does not erase all records described above. For privacy or deletion questions, contact{" "}
            <a href="mailto:privacy@liveswell.io" className="text-emerald-400 underline hover:text-emerald-300">
              privacy@liveswell.io
            </a>.
          </p>
        </Section>

        <Section title="Your settings and choices">
          <p>
            Use Profile → Notifications to manage alerts and delivery channels, and your device or
            browser settings to control notification and location permissions. You can search for
            a surf spot without granting location access. Sign-in sessions and app preferences may
            use cookies or browser storage; blocking these can affect sign-in and app features.
          </p>
          <p>
            Use New chat in Agent to clear in-app history, STOP to opt out of SMS to your number,
            Account &amp; Billing to manage subscriptions and view reply billing history, or Delete my account
            to request account removal. These are separate actions.
          </p>
        </Section>

        <Section title="Security">
          <p>
            LiveSwell uses authenticated access controls and HTTPS connections for the public app.
            Clerk handles current account sign-in. These measures do not guarantee that data can
            never be accessed without authorization. Do not send sensitive information in Agent
            questions or support messages.
          </p>
        </Section>

        <Section title="Children's privacy">
          <p>
            LiveSwell is not directed at children under 13. We do not knowingly collect personal
            information from children under 13. If you believe a child has provided us with personal
            information, please contact us so we can investigate and address the request.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will revise the
            "Last updated" date at the top of this page.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            For privacy-related questions or help with account deletion, contact us at:
          </p>
          <div className="rounded-xl px-4 py-3 space-y-0.5"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <p className="text-slate-200 font-semibold">LiveSwell</p>
            <p>
              <a href="mailto:privacy@liveswell.io" className="text-emerald-400 underline hover:text-emerald-300">
                privacy@liveswell.io
              </a>
            </p>
            <p>
              <a href="https://liveswell.io" className="text-emerald-400 underline hover:text-emerald-300">
                liveswell.io
              </a>
            </p>
          </div>
        </Section>

      </main>

      <Footer />
    </div>
  );
}
